import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import { useCreateAppExclusiveSchedule } from '~/hooks/apis/offers/useCreateAppExclusiveSchedule';
import { useGetEligibleAppExclusiveOffers } from '~/hooks/apis/offers/useGetEligibleAppExclusiveOffers';
import useDebounced from '~/hooks/useDebounced';

import { formatSlotTime, getOfferBlockReason, getOnSaleEnd, todayDate } from './appOnlyFlashCalendar';

const DATE_FORMAT = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 300;
// Enough to scan a day's options; the search box narrows the rest.
const ELIGIBLE_OFFER_LIMIT = 50;

interface Props {
  open: boolean;
  region: string;
  regionName: string;
  /** Sydney calendar day (YYYY-MM-DD) to open on. */
  initialDate: string;
  onClose: () => void;
}

export default function AddAppOnlyFlashDealDialog({ open, region, regionName, initialDate, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(initialDate);
  const [search, setSearch] = useState('');
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const debouncedSearch = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);

  // Each open starts fresh on the day the admin clicked.
  useEffect(() => {
    if (open) {
      setDate(initialDate);
      setSearch('');
      setSelectedOfferId(null);
    }
  }, [open, initialDate]);

  const { data, isFetching, isError, error } = useGetEligibleAppExclusiveOffers(
    { region, date, q: debouncedSearch || undefined, limit: ELIGIBLE_OFFER_LIMIT },
    open,
  );
  const createSchedule = useCreateAppExclusiveSchedule();

  const offers = data?.offers ?? [];
  const selectedOffer = offers.find((offer) => offer.offerId === selectedOfferId && offer.canSchedule);

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setDate(value.format(DATE_FORMAT));
      setSelectedOfferId(null);
    }
  };

  const handleSubmit = async () => {
    if (!data || !selectedOffer) {
      return;
    }
    try {
      await createSchedule.mutateAsync({
        offerId: selectedOffer.offerId,
        region,
        start: data.slot.start,
        end: data.slot.end,
      });
      enqueueSnackbar(
        `${selectedOffer.name ?? selectedOffer.offerId} scheduled for ${formatSlotTime(data.slot.start)}`,
        {
          variant: 'success',
        },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(`Could not schedule deal: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="add-app-only-flash-deal-title">
      <DialogTitle id="add-app-only-flash-deal-title">Add App Only Flash deal · {regionName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Day"
              format="ddd D MMM YYYY"
              value={dayjs(date, DATE_FORMAT)}
              minDate={dayjs(todayDate(), DATE_FORMAT)}
              onChange={handleDateChange}
              slotProps={{ textField: { required: true, sx: { minWidth: 220 } } }}
            />
            <TextField
              label="Search offer name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              fullWidth
            />
          </Stack>

          {data && (
            <Typography variant="body2" color="text.secondary">
              Slot runs {formatSlotTime(data.slot.start)} to {formatSlotTime(data.slot.end)} ({APP_EXCLUSIVE_TIMEZONE}).
              The deal must stay on sale until {formatSlotTime(data.slot.onSaleUntil)}.
            </Typography>
          )}

          {isError && <Alert severity="error">Could not load eligible offers: {error?.message}</Alert>}
          {data?.dayTakenBy && (
            <Alert severity="warning">
              This day is already taken in {region} by offer {data.dayTakenBy.offerId}. Pick another day.
            </Alert>
          )}

          <Box sx={{ minHeight: 240, maxHeight: 420, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
            {isFetching && !data && (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {data && offers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No offline flash hotels are on sale for this slot
                {debouncedSearch ? ` matching "${debouncedSearch}"` : ''}.
              </Typography>
            )}
            {offers.length > 0 && (
              <List dense disablePadding>
                {offers.map((offer) => {
                  const blockReason = getOfferBlockReason(offer);
                  const isSelected = offer.offerId === selectedOfferId;
                  return (
                    <ListItemButton
                      key={offer.offerId}
                      selected={isSelected}
                      disabled={!!blockReason}
                      onClick={() => setSelectedOfferId(offer.offerId)}
                      divider
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Radio edge="start" checked={isSelected} disabled={!!blockReason} tabIndex={-1} />
                      </ListItemIcon>
                      <ListItemText
                        primary={offer.name ?? offer.offerId}
                        secondary={[
                          offer.location,
                          blockReason ?? `On sale until ${formatSlotTime(getOnSaleEnd(offer))}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {data && data.total > offers.length && (
            <Typography variant="caption" color="text.secondary">
              Showing {offers.length} of {data.total} offers. Search by name to narrow the list.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedOffer || createSchedule.isPending}>
          {createSchedule.isPending ? 'Adding...' : 'Add deal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
